import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Save, Upload, AlertTriangle, Server } from "lucide-react";
import { useFiscalConfig } from "@/hooks/useFiscalConfig";
import { AMBIENTES_NFCE, INTEGRADORES } from "@/lib/fiscal-constants";
import { AcbrConnectionTest } from "./AcbrConnectionTest";

export function FiscalConfigCard() {
  const { config, loading, createOrUpdateConfig } = useFiscalConfig();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    csc_id: "",
    csc_token: "",
    nfce_serie: 1,
    ambiente: "homologacao",
    integrator: "",
    integrator_token: "",
    acbr_host: "localhost",
    acbr_port: 3434,
    acbr_timeout_ms: 90000,
    acbr_ini_path: "C:\\ACBrMonitorPLUS\\nfce_temp.ini",
  });

  useEffect(() => {
    if (config) {
      setFormData({
        csc_id: config.csc_id || "",
        csc_token: config.csc_token || "",
        nfce_serie: config.nfce_serie || 1,
        ambiente: config.ambiente || "homologacao",
        integrator: config.integrator || "",
        integrator_token: config.integrator_token || "",
        acbr_host: (config as any).acbr_host || "localhost",
        acbr_port: (config as any).acbr_port || 3434,
        acbr_timeout_ms: (config as any).acbr_timeout_ms || 90000,
        acbr_ini_path: (config as any).acbr_ini_path || "C:\\ACBrMonitorPLUS\\nfce_temp.ini",
      });
    }
  }, [config]);

  const handleSave = async () => {
    setSaving(true);
    await createOrUpdateConfig(formData as any);
    setSaving(false);
  };

  const isAcbrSelected = formData.integrator === "acbr_monitor";

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/4" />
            <div className="h-10 bg-muted rounded" />
            <div className="h-10 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Integrador de API */}
      <Card>
        <CardHeader>
          <CardTitle>Integrador de Emissão</CardTitle>
          <CardDescription>
            Serviço utilizado para comunicação com a SEFAZ
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="integrator">Integrador</Label>
              <Select
                value={formData.integrator}
                onValueChange={(value) => setFormData({ ...formData, integrator: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {INTEGRADORES.map((int) => (
                    <SelectItem key={int.value} value={int.value}>
                      {int.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!isAcbrSelected && (
              <div className="space-y-2">
                <Label htmlFor="integrator_token">Token da API</Label>
                <Input
                  id="integrator_token"
                  type="password"
                  value={formData.integrator_token}
                  onChange={(e) => setFormData({ ...formData, integrator_token: e.target.value })}
                  placeholder="Token de acesso"
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ACBrMonitor - Apenas se selecionado */}
      {isAcbrSelected && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-5 w-5" />
              ACBrMonitorPlus
            </CardTitle>
            <CardDescription>
              Configuração de conexão com o ACBrMonitor instalado localmente
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <AcbrConnectionTest
              host={formData.acbr_host}
              port={formData.acbr_port}
              timeoutMs={formData.acbr_timeout_ms}
              onHostChange={(host) => setFormData({ ...formData, acbr_host: host })}
              onPortChange={(port) => setFormData({ ...formData, acbr_port: port })}
              onTimeoutChange={(timeoutMs) => setFormData({ ...formData, acbr_timeout_ms: timeoutMs })}
            />
            
            <Separator />
            
            <div className="space-y-2">
              <Label htmlFor="acbr_ini_path">Caminho do arquivo INI no servidor</Label>
              <Input
                id="acbr_ini_path"
                value={formData.acbr_ini_path}
                onChange={(e) => setFormData({ ...formData, acbr_ini_path: e.target.value })}
                placeholder="C:\ACBrMonitorPLUS\nfce_temp.ini"
              />
              <p className="text-xs text-muted-foreground">
                O sistema gerará o arquivo INI neste caminho no servidor Windows. 
                Certifique-se de que a pasta existe e o ACBr tem permissão de leitura.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Certificado Digital */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Certificado Digital A1
          </CardTitle>
          <CardDescription>
            {isAcbrSelected 
              ? "O certificado deve ser configurado diretamente no ACBrMonitor"
              : "O certificado é gerenciado pelo integrador de API"
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            {config?.certificate_uploaded ? (
              <Badge variant="default" className="bg-green-600">
                Certificado configurado
              </Badge>
            ) : (
              <Badge variant="secondary">
                Não configurado
              </Badge>
            )}
            {config?.certificate_expires_at && (
              <span className="text-sm text-muted-foreground">
                Válido até: {new Date(config.certificate_expires_at).toLocaleDateString("pt-BR")}
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            {isAcbrSelected
              ? "Abra o ACBrMonitor no servidor e configure o certificado A1 na aba NFe/NFCe."
              : "O certificado digital deve ser configurado diretamente no painel do integrador escolhido."
            }
          </p>
        </CardContent>
      </Card>

      {/* Configurações NFC-e */}
      <Card>
        <CardHeader>
          <CardTitle>Configurações NFC-e</CardTitle>
          <CardDescription>
            Parâmetros para emissão de notas fiscais
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="csc_id">CSC ID</Label>
              <Input
                id="csc_id"
                value={formData.csc_id}
                onChange={(e) => setFormData({ ...formData, csc_id: e.target.value })}
                placeholder="Ex: 1"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="csc_token">CSC Token</Label>
              <Input
                id="csc_token"
                type="password"
                value={formData.csc_token}
                onChange={(e) => setFormData({ ...formData, csc_token: e.target.value })}
                placeholder="Token do CSC"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nfce_serie">Série NFC-e</Label>
              <Input
                id="nfce_serie"
                type="number"
                min={1}
                value={formData.nfce_serie}
                onChange={(e) => setFormData({ ...formData, nfce_serie: parseInt(e.target.value) || 1 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Próximo Número</Label>
              <Input
                value={config?.nfce_numero_atual ? config.nfce_numero_atual + 1 : 1}
                disabled
                className="bg-muted"
              />
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="ambiente">Ambiente</Label>
            <Select
              value={formData.ambiente}
              onValueChange={(value) => setFormData({ ...formData, ambiente: value })}
            >
              <SelectTrigger className="w-full sm:w-[300px]">
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent>
                {AMBIENTES_NFCE.map((amb) => (
                  <SelectItem key={amb.value} value={amb.value}>
                    {amb.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formData.ambiente === "homologacao" && (
              <p className="text-sm text-yellow-600 flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" />
                Ambiente de testes. Notas não têm validade fiscal.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Botão Salvar */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? "Salvando..." : "Salvar Configurações"}
        </Button>
      </div>
    </div>
  );
}
